/**
 * Pure helpers for artwork renditions — downscaled JPEG copies of a piece that
 * live in Storage next to the original, addressed purely by naming convention.
 * No Firestore field records them; the lookup is `getRenditionPath` + a
 * Storage existence check (see `useRenditionUrl`).
 *
 * The object path is always derived from `artwork.url`, never `artwork.path`:
 * in prod `artwork.*.path` sometimes holds a directory prefix rather than an
 * object path (see `fixtures/pipeline/hydrate.ts`).
 *
 * No DOM or Firebase imports — everything here is unit-testable in node.
 */
import { compact, uniq } from "lodash"

import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"

export const RENDITION_TIER = {
  THUMB: "thumb",
  MED: "med",
} as const

export type RenditionTier = (typeof RENDITION_TIER)[keyof typeof RENDITION_TIER]

/** Longest edge in px. Grid tiles get THUMB, the full-height strip gets MED. */
export const RENDITION_LONG_EDGE: Record<RenditionTier, number> = {
  [RENDITION_TIER.THUMB]: 400,
  [RENDITION_TIER.MED]: 1200,
}

export const RENDITION_EXTENSION = "jpg"

// Duplicated in server/mobile/mobileDomain.ts — path aliases don't cross
// workspaces. Keep the two copies in sync (the server test cases are mirrored
// in artworkRenditionHelpers.test.ts).
export const parseStoragePathFromUrl = (url: string): string | null => {
  const marker = "/o/"
  const idx = url.indexOf(marker)
  if (idx === -1) return null
  const rest = url.slice(idx + marker.length)
  const queryIdx = rest.indexOf("?")
  const encoded = queryIdx === -1 ? rest : rest.slice(0, queryIdx)
  if (!encoded) return null
  try {
    return decodeURIComponent(encoded)
  } catch {
    return null
  }
}

/**
 * Bucket segment of a Firebase Storage download URL
 * (`.../v0/b/{bucket}/o/...`). Works for prod hosts and the emulator's
 * `http://127.0.0.1:9199/v0/b/...` form alike.
 */
export const parseStorageBucketFromUrl = (url: string): string | null => {
  const marker = "/v0/b/"
  const idx = url.indexOf(marker)
  if (idx === -1) return null
  const rest = url.slice(idx + marker.length)
  const end = rest.indexOf("/")
  const bucket = end === -1 ? rest : rest.slice(0, end)
  return bucket ? bucket : null
}

/**
 * `dir/name.ext` → `dir/name_thumb.jpg`. Only the final path segment is
 * inspected for an extension, so dotted directory names survive intact.
 * Always `.jpg`: a PNG downscale of a scan is several times the bytes.
 */
export const getRenditionPath = (
  objectPath: string,
  tier: RenditionTier
): string => {
  const slash = objectPath.lastIndexOf("/")
  const dir = slash === -1 ? "" : objectPath.slice(0, slash + 1)
  const name = slash === -1 ? objectPath : objectPath.slice(slash + 1)
  const dot = name.lastIndexOf(".")
  const stem = dot > 0 ? name.slice(0, dot) : name
  return `${dir}${stem}_${tier}.${RENDITION_EXTENSION}`
}

/**
 * The full-size image a display surface should show when no rendition is
 * available. Mobile crops win over the original per `StudentArtwork.cropUrl`.
 */
export const getDisplayOriginalUrl = (
  artwork: Pick<StudentArtwork, "url" | "cropUrl">
): string => artwork.cropUrl ?? artwork.url

export type StoredArtworkUrlPreference = "tile" | "full"

const storedUrl = (value: string | null | undefined): string =>
  value?.trim() ?? ""

/**
 * A download URL already stored on the artwork. Paint this directly — do not
 * wait on Storage `getDownloadURL`. An empty string means none of `cropUrl`,
 * `thumbnailUrl`, or `url` is set, and the tile shows a placeholder.
 *
 * Tile order prefers the crop, then the stored thumbnail, then the original.
 * Full order prefers the crop, then the original, then the thumbnail.
 */
export const getStoredArtworkUrl = (
  artwork:
    | Pick<StudentArtwork, "url" | "cropUrl" | "thumbnailUrl">
    | null
    | undefined,
  preference: StoredArtworkUrlPreference = "tile"
): string => {
  if (!artwork) return ""
  const crop = storedUrl(artwork.cropUrl)
  const thumb = storedUrl(artwork.thumbnailUrl)
  const original = storedUrl(artwork.url)
  const ordered =
    preference === "full" ? [crop, original, thumb] : [crop, thumb, original]
  return ordered.find((url) => url.length > 0) ?? ""
}

/**
 * Renditions are only written next to originals this app owns: not mobile
 * records (they already carry `thumbnailUrl` and live in another bucket) and
 * not legacy scanner records whose URL points at a different bucket.
 */
export const isRenditionWritable = (
  artwork: Pick<StudentArtwork, "url" | "storageOrigin" | "cropUrl">,
  appBucket: string | null | undefined
): boolean => {
  if (!appBucket) return false
  if (artwork.storageOrigin === "mobile") return false
  if (artwork.cropUrl) return false
  if (parseStorageBucketFromUrl(artwork.url) !== appBucket) return false
  return parseStoragePathFromUrl(artwork.url) !== null
}

/**
 * Accepts a `ZOOM_LEVELS` entry (a column count or `"strip"`): the strip is
 * the only surface that shows a piece large enough to need the MED tier.
 */
export const getRenditionTierForZoom = (
  zoomValue: number | "strip"
): RenditionTier =>
  zoomValue === "strip" ? RENDITION_TIER.MED : RENDITION_TIER.THUMB

export interface ScaledDimensions {
  width: number
  height: number
}

/** Fits the longest edge to `longEdge`, never upscaling. */
export const getScaledDimensions = (
  width: number,
  height: number,
  longEdge: number
): ScaledDimensions => {
  const longest = Math.max(width, height)
  if (longest <= longEdge || longest <= 0) return { width, height }
  const scale = longEdge / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export interface ArtworkSrcPlan {
  /** Storage object path to probe, or null when this piece cannot have one. */
  renditionPath: string | null
  /** Ordered URLs to fall back to when the rendition is missing or pending. */
  fallbacks: string[]
}

/**
 * Pure half of the "convention → thumbnailUrl → original" chain. Whether the
 * rendition object exists is only knowable asynchronously, so this returns the
 * path to probe plus the fallbacks; `resolveArtworkSrc` combines the two.
 */
export const planArtworkSrc = (
  artwork: Pick<
    StudentArtwork,
    "url" | "cropUrl" | "thumbnailUrl" | "storageOrigin"
  >,
  tier: RenditionTier,
  appBucket: string | null | undefined
): ArtworkSrcPlan => {
  const objectPath = isRenditionWritable(artwork, appBucket)
    ? parseStoragePathFromUrl(artwork.url)
    : null
  const renditionPath = objectPath ? getRenditionPath(objectPath, tier) : null
  const original = getDisplayOriginalUrl(artwork)
  const fallbacks =
    tier === RENDITION_TIER.THUMB
      ? uniq(compact([artwork.thumbnailUrl, original]))
      : [original]
  return { renditionPath, fallbacks }
}

/**
 * `renditionUrl` is `undefined` while the lookup is pending, `null` on a miss,
 * or the resolved download URL. Pending still yields the first fallback so a
 * tile paints immediately instead of waiting a round trip.
 */
export const resolveArtworkSrc = (
  plan: ArtworkSrcPlan,
  renditionUrl: string | null | undefined
): string => {
  if (plan.renditionPath && typeof renditionUrl === "string") {
    return renditionUrl
  }
  return plan.fallbacks[0] ?? ""
}
